import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { EquationPartTypes } from '../../../models/risk-equation-part-type';
import { AgilityRiskFormulaHelperService } from '../../../providers/agility-risk-formula-helper.service';
import { RiskFactor } from '../../../models/risk-factor';
import { RiskEquationPart } from '../../../models/risk-formula';
import { RiskFormulaType } from '../../../models/risk-formula-type';

@Component({
  selector: 'lib-risk-formula-builder',
  templateUrl: './risk-formula-builder.component.html',
  styleUrls: ['./risk-formula-builder.component.scss']
})
export class RiskFormulaBuilderComponent implements OnInit{
  @Input() formula: Array<RiskEquationPart>;
  @Input() riskFactors: Array<RiskFactor>;
  @Input() formulaType: RiskFormulaType;
  @Output() formulaChangedAndValid = new EventEmitter<null>();

  @ViewChildren('input', {read: AutoComplete}) inputs: QueryList<AutoComplete>;

  riskFormulaType = RiskFormulaType;
  formulaPartTypes = EquationPartTypes;
  formulaUIParts: Array<any>;

  searchResults: Array<string>;
  isFormulaValid = true;
  isInBlurEvent = false;

  formulaString: string;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private formulaHelper: AgilityRiskFormulaHelperService) {
  }

  ngOnInit() {
    this.formulaString = this.formulaHelper.getFormulaAsString(this.formula);
  }

  onPreElemClicked(index: number) {
    if (this.allowEdit) {
      this.sanitizeFormula();
      this.addFormulaPart(index);
    }
  }

  onLastElemClicked() {
    if (this.allowEdit) {
      this.sanitizeFormula();
      this.addFormulaPart(this.formula.length);
    }
  }

  onElemClicked(index: number) {
    if (this.allowEdit &&
    !this.isInBlurEvent) {
      if (this.formula[index].isEdit) {
        return;
      }
      this.sanitizeFormula();
      this.editFormulaPart(index);
    }
  }

  get allowEdit() {

    return !this.isEditing ||
      // if empty field, allow switching
      !(this.formula.find(fp => fp.isEdit).label);
  }

  editFormulaPart(index: number) {
    if (this.formula[index].type === EquationPartTypes.factor &&
        this.formulaType === RiskFormulaType.multiPart) {
      return;
    }
    this.formula.forEach((fp, i) => fp.isEdit = index === i);

    this.startSetFocus();
  }

  onKeyPress(event: any, index: number) {
    const res = this.formulaType === RiskFormulaType.multiPart ? this.keyPressMultiPart(event, index) : this.keyPress(event, index);

    this.evalFormula();
    return res;
  }

  keyPressMultiPart(event: any, index: number) {
    if (event.keyCode === 13) { // enter
      this.sanitizeFormula();
      return true;
    }

    // if operator entered - allow only + and * for multipart
    const operator = this.formulaHelper.getOperator(event.keyCode);
    if (!operator ||
        operator.key === '-' ||
        operator.key === '/') {
      return false;
    }
    if (operator) {
      this.formula[index].type = EquationPartTypes.operator;
      this.formula[index].isEdit = false;
      this.formula[index].label = operator.key;
      return true;
    }
  }

  keyPress(event: any, index: number) {
    if (event.keyCode === 13) { // enter
      this.sanitizeFormula();

      // if entering factor, validate that the factor is valid.
      if (this.isEquationPartOfFactorType(index)) {
        if(this.completeFactorProcessing(index)) {
          this.addFormulaPart(++index);
        } else {
          return false;
        }
      }

      if (this.isEquationPartOfOperatorType(index)) {
        this.addFormulaPart(++index);
        return true;
      }

      return true;
    }

    // if digit entered - needs to be checked before any other character
    const digit = this.formulaHelper.getDigit(event.keyCode);
    // if digit and part is already a number or the type is not set yet
    const isNumber = !this.formula[index].label?.length || !isNaN(parseFloat(this.formula[index].label));
    if (digit &&
      isNumber) {
      this.formula[index].type = EquationPartTypes.number;
      return true;
    }

    // if operator entered
    const operator = this.formulaHelper.getOperator(event.keyCode);
    if (operator) {
      return this.processOperator(index, operator);
    }

    // if parenthesis entered
    const parenthesise = this.formulaHelper.getParenthesise(event.keyCode);
    if (parenthesise) {
      return this.processParenthesis(index, parenthesise);
    }

    // adding factor
    return this.processFactor(index);
  }

  onKeyUp(event: any, index: number) {
    if (event.keyCode === 27 || // Escape
        event.keyCode === 13) // Enter
    {
      this.sanitizeFormula();
      if (this.isEquationPartOfFactorType(index)) {
        if (this.completeFactorProcessing(index)) {
          this.addFormulaPart(++index);
        } else {
          this.formula[index].isEdit = true;
        }
      }
      return;
    }
  }

  processOperator(index: number, operator: any) {
    // cannot start formula with operator
    if (index === 0 &&
        !(this.formula[0].label?.length)) {
      return false;
    }
    // after digit could be an operand, so
    // if was entering digits, but now entered operator - add operator and add next part
    if (this.formula[index].type === EquationPartTypes.number) {
      this.formula[index].isEdit = false;
      this.addFormulaPart(++index);
    }

    // after digit could be an operand, so
    // if was entering digits, but now entered operator - add operator and add next part
    if (this.isEquationPartOfFactorType(index)) {
      if (this.completeFactorProcessing(index)) {
        this.addFormulaPart(++index);
      } else {
        return false;
      }
    }

    if (this.formula[index].type === EquationPartTypes.parenthesise) {
      // should not add operator after left parenthesis
      if (this.formula[index].label === this.formulaHelper.parenthesiseKeys.leftParenth.key) {
        return false;
      }
      this.formula[index].isEdit = false;
      this.addFormulaPart(++index);
    }

    // new part initialization
    this.formula[index].type = EquationPartTypes.operator;
    this.formula[index].isEdit = false;
    this.formula[index].label = operator.key;
    // add new formula part if operator added
    this.addFormulaPart(++index);
    return true;
  }

  processParenthesis(index: number, parenthesise: any) {
    // cannot start formula with right parenthesis
    if (index === 0 &&
      !(this.formula[index].label?.length) &&
      parenthesise.id === this.formulaHelper.parenthesiseKeys.rightParenth.id) {
      return false;
    }

    // was adding factor, finish and add parenthesis
    if (this.isEquationPartOfFactorType(index)) {
      if (this.completeFactorProcessing(index)) {
        this.addFormulaPart(++index);
      } else {
        return false;
      }
    }

    // after digit could be right parenthesis, so
    // if was entering digits, but now entered right parenthesis - add parenthesis and add next part
    if (this.formula[index].type === EquationPartTypes.number) {
      this.formula[index].isEdit = false;
      this.addFormulaPart(++index);
    }

    this.formula[index].type = EquationPartTypes.parenthesise;
    this.formula[index].isEdit = false;
    this.formula[index].label = parenthesise.key;
    // add new formula part if parenthesise added
    this.addFormulaPart(++index);
    return true;
  }

  processFactor(index: number) {
    // factor name can may have a digit, including beginning and end!

    // previous equation part cannot be right parenthesis
    // or digit
    if (index > 0 &&
      (this.formulaHelper.isRightParenthesise(this.formula[index - 1]) ||
        this.formula[index - 1].type === EquationPartTypes.number)) {
      return false;
    }

    this.formula[index].type = EquationPartTypes.factor;
    return true;
  }

  isEquationPartOfFactorType(index: number) {
    if (index >= this.formula.length) {
      return false;
    }
    return this.formula[index].type === EquationPartTypes.factor;
  }

  isEquationPartOfOperatorType(index: number) {
    if (index >= this.formula.length) {
      return false;
    }
    return this.formula[index].type === EquationPartTypes.operator;
  }

  completeFactorProcessing(index: number) {

    // try to match factor
    const factor = this.getFactor(this.formula[index].label);
    if (factor) {
      // valid factor
      this.formula[index].factorId = factor.id;
      this.formula[index].label = factor.name;
      this.formula[index].isEdit = false;
      return true;
    } else {
      return false;
    }
  }

  getFactor(name: string): RiskFactor {
    const factors = this.riskFactors?.filter(rf => rf.name.indexOf(name) >= 0);
    if (factors?.length > 0) {
      return factors[0];
    }
    return null;
  }

  setFormulaFactorsId() {
    for( let i = this.formula.length - 1; i >= 0; i--) {
      if (this.formula[i].type === EquationPartTypes.factor &&
          this.formula[i].label) {
        // set factor id for the equation part
        const factor = this.riskFactors?.find(rf => rf.name === this.formula[i].label);
        if (factor) {
          this.formula[i].factorId = factor.id;
        }
      }
    }
  }

  sanitizeFormula() {
    // remove empty parts
    for( let i = this.formula.length - 1; i >= 0; i--) {
      if (!this.formula[i].label &&
        this.formulaType !== RiskFormulaType.multiPart) {
        this.formula.splice(i, 1);
      }
    }

    this.formula.forEach((f, index) => {
      f.order = index;

      if (this.formulaType === RiskFormulaType.multiPart &&
          f.type === EquationPartTypes.operator &&
          !(f.label)) {
        f.isEdit = true;
      } else {
        f.isEdit = false;
      }
    });

    this.evalFormula();
  }

  search(event: any, index: number) {
    if (this.formulaType === RiskFormulaType.multiPart) {
      this.searchResults = [];
      return;
    }

    if (!event ||
      !this.riskFactors?.length) {
      this.searchResults = [];
      return;
    }

    this.searchResults = this.riskFactors.filter(rf => rf.name.toLowerCase().indexOf(event.query.toLowerCase()) >= 0)?.map(rf => rf.name);
  }

  addFormulaPart(index: number) {
    this.formula.splice(index, 0, {
      order: 0,
      label: null,
      type: null,
      isEdit: true,
      factorId: null
    });

    this.startSetFocus();
  }

  startSetFocus() {
    setTimeout(this.setFocus.bind(this), 200);
  }

  setFocus() {
    this.changeDetectorRef.detectChanges();
    this.inputs.first.inputEL.nativeElement.focus();
  }

  onSelect(index: number) {
    // this.sanitizeFormula();
    // select is triggered by autofill selection,
    // so the type is factor
    this.formula[index].type = EquationPartTypes.factor;
    this.formula[index].isEdit = false;
    this.addFormulaPart(++index);
    this.evalFormula();
  }

  onACBlur(index: number) {
    // set flag so onElementClicked and onSelect is processed accordingly
    this.isInBlurEvent = true;
    setTimeout(this.onBlurTimer.bind(this), 500, index);

  }

  onBlurTimer(index: number) {
    // if input is empty - end editing
    if (this.isEmptyFormulaInput(index)) {
      this.formula[index].isEdit = false;
      this.sanitizeFormula();
    } else { // not empty input
      // check if formula is valid
      this.evalFormula();
      // if formula is valid, stop editing
      if (this.isFormulaValid) {
        this.formula[index].isEdit = false;
        this.sanitizeFormula();
      }
    }
    this.isInBlurEvent = false;
  }

  onClear(index: number) {
    this.formula[index].type = null;
  }

  isEmptyFormulaInput(index: number): boolean {
    return index >= this.formula.length ||
      !this.formula[index].label;
  }

  get isEditing() {
    return this.formula.some(fp => fp.isEdit);
  }

  evalFormula() {
    this.setFormulaFactorsId();
    // if left and right parenthesise numbers do not match - failed
    if (this.formula.filter(fp => this.formulaHelper.isLeftParenthesise(fp)).length !==
      this.formula.filter(fp => this.formulaHelper.isRightParenthesise(fp)).length) {
      this.isFormulaValid = false;
      return;
    }

    const formulaToReduce: Array<any> = this.formula.map(f => f);



    const code = formulaToReduce.length === 1 ?
      this.getEquationPartTestValue(formulaToReduce[0]) :
      formulaToReduce.filter(fp => fp.label?.length).reduce((a, b) => {
        try {
          let fp1 = '';
          if (typeof a === 'string') {
            fp1 = a;
          } else {
            fp1 = this.getEquationPartTestValue(a);
          }
          const fp2 = this.getEquationPartTestValue(b);
          return fp1.toString() + fp2.toString();
        } catch(ex) {
          return '';
        }
      }).toString();

    try {
      const result: number = Function(`return (${code})`)();
      this.isFormulaValid = !!(result) || result === 0;
      if (this.isFormulaValid) {
        const newFormulaString = this.formulaHelper.getFormulaAsString(this.formula);
        if (newFormulaString !== this.formulaString) {
          this.formulaString = newFormulaString;
          this.formulaChangedAndValid.emit();
        }
      }
    } catch(ex) {
      this.isFormulaValid = false;
    }
  }

  getEquationPartTestValue(equationPart: RiskEquationPart) {
    let res: string;
    if (equationPart.type === EquationPartTypes.factor) {
      if (this.formulaType === RiskFormulaType.multiPart) {
        res = '2'; // just hardcode any value
      } else {
        res =  ((this.riskFactors.find(rf => rf.name === equationPart.label)?.ranks?.length + 1) / 2).toString();
      }
    } else {
      res = equationPart.label;
    }

    return `${res} `;
  }
}
